import { useMemo } from 'react';

import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { OneLineHeader } from '@/components/Table/TableHeader';
import { RightAlignedTruncatedCell, TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useDiscoveryAppliedSeeds } from '@/hooks/discovery/appliedSeeds';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { formatAbsoluteLocalDate } from '@/util/date';
import { ResourceStat } from '@/util/discoveryStat';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string;
  uniqueId: string;
  lastExecuted: string | null;
  rowCount: string | undefined;
};

type SeedAppliedListNode = {
  uniqueId: string;
  executionInfo: {
    executeCompletedAt: string | null;
  } | null;
  catalog: {
    rowCountStat: number | null;
    stats: ResourceStat[] | null;
  } | null;
};

const getTableData = (seed: SeedAppliedListNode): TableData => {
  const uniqueId = parseUniqueId(seed.uniqueId);
  const executeCompletedAtStr = seed.executionInfo?.executeCompletedAt;
  const executedAtDate = executeCompletedAtStr ? new Date(executeCompletedAtStr) : null;
  const lastExecuted = executedAtDate && formatAbsoluteLocalDate(executedAtDate);
  // TODO: add tooltip for UTC time
  // const lastExecutedUtc = executedAtDate && formatDateCommon(executedAtDate).utc;
  return {
    name: uniqueId.remainder,
    uniqueId: uniqueId.raw,
    lastExecuted,
    rowCount: seed.catalog?.rowCountStat?.toLocaleString(),
  };
};

export const SeedFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const paginatedSeedQuery = useDiscoveryAppliedSeeds({
    environmentId,
    userId: userId as number | null,
    first: pageSize,
    pause: userId === 'loading',
  });
  const seeds = paginatedSeedQuery.data;
  const pageInfo = paginatedSeedQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return seeds?.map(getTableData);
  }, [seeds]);

  if (paginatedSeedQuery.error) return <>{paginatedSeedQuery.error.stack}</>;
  const isLoadingMore = paginatedSeedQuery.fetching;
  const isInitialLoad =
    paginatedSeedQuery.fetching && paginatedSeedQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Seeds</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="seeds"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: () => <OneLineHeader title="Row count" align="right" />,
            accessorKey: 'rowCount',
            minSize: 120,
            size: 40,
            cell: RightAlignedTruncatedCell,
            meta: {
              align: 'right',
            },
          },
          {
            header: 'Last executed',
            accessorKey: 'lastExecuted',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedSeedQuery.fetchMore}
      />
    </>
  );
};
